import type { Patient } from '@dataTypes/PatientInfo.types'
import { AddNewPatient, CheckPatientDuplicate } from '@services/api/patient.api'
import { ApiError } from '@services/api/errors/api-error'
import {
  cleanEmptyPatientFields,
  normalizePatientForSave,
} from '@components/layout/Modals/AddPatientModal/patientForm.logic'
import { defaultPatientData } from '@components/layout/Modals/AddPatientModal/patientDataDefaults'

export type KioskIdentityChoice = 'new' | 'existing'
export type KioskIdentityView = 'new-patient' | 'otp'
export type KioskIdentificationMode = KioskIdentityChoice | null

export const viewForIdentityChoice = (
  choice: KioskIdentityChoice,
): KioskIdentityView => (choice === 'new' ? 'new-patient' : 'otp')

export const emptyAssignmentsView = (
  mode: KioskIdentificationMode,
): 'registration-complete' | 'forms' =>
  mode === 'new' ? 'registration-complete' : 'forms'

export const freshKioskPatientData = (): Patient => ({
  ...defaultPatientData,
  allergy_ids: [],
  medical_condition_ids: [],
  patient_allergies: [],
  patient_medical_conditions: [],
  uploadedFiles: [],
  contact_via: [],
})

type CreatePatient = typeof AddNewPatient
type CheckDuplicate = typeof CheckPatientDuplicate

export type RegistrationResult =
  | { kind: 'created'; patientId: string }
  | { kind: 'duplicate' }

export const createKioskPatient = async (
  patientData: Patient,
  clinicId: string,
  accessToken: string,
  actorId: string,
  createPatient: CreatePatient = AddNewPatient,
  checkDuplicate: CheckDuplicate = CheckPatientDuplicate,
): Promise<RegistrationResult> => {
  try {
    const normalized = normalizePatientForSave(patientData, clinicId)
    const requestOptions = { accessToken, clinicId }
    const duplicateResponse = await checkDuplicate(
      {
        first_name: normalized.first_name,
        last_name: normalized.last_name,
        date_of_birth: normalized.date_of_birth,
        cellphone: normalized.cellphone,
        email: normalized.email,
      },
      requestOptions,
    )
    if (duplicateResponse.data.duplicate) return { kind: 'duplicate' }

    const response = await createPatient(
      {
        ...cleanEmptyPatientFields(normalized),
        user_id: actorId,
        created_by: actorId,
        updated_by: actorId,
      },
      requestOptions,
    )
    const patientId = response.data?.patient?.patient_id?.trim()
    if (!patientId) throw new Error('Patient creation did not return an ID.')
    return { kind: 'created', patientId }
  } catch (error) {
    if (
      error instanceof ApiError &&
      (error.statusCode === 409 || error.errorCode === 'PATIENT_DUPLICATE')
    ) {
      return { kind: 'duplicate' }
    }
    throw error
  }
}

export type KioskSubmitPhase = 'success' | 'error' | 'partial'

export const kioskSubmitOutcome = (
  completedCount: number,
  failedCount: number,
  total: number,
): { phase: KioskSubmitPhase; message: string } => {
  if (failedCount === 0 && completedCount > 0) {
    return { phase: 'success', message: 'Your forms are now saved.' }
  }
  if (completedCount === 0) {
    return {
      phase: 'error',
      message: 'Could not save your forms. Please ask staff for assistance.',
    }
  }
  return {
    phase: 'partial',
    message: `${completedCount} of ${total} forms were saved. The rest were not saved. Please ask staff for assistance.`,
  }
}
